import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { TestSet } from "../../model_gen/evaluation";
import { TestSetService } from "../../services/TestSetService";
import { NEW_TEST_SET_STORAGE_KEY } from "./TestSetNew";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { PlusIcon } from "lucide-react";
import { notification, message } from "antd";
import TestSetsTable from "../shared/TestSetsTable";

interface SortConfig {
  key: keyof TestSet | null;
  direction: "asc" | "desc";
}

const TestSets: React.FC = () => {
  const { apiKey } = usePermissions();
  const { intentGroupId } = useParams<{ intentGroupId?: string }>();
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [allTestSets, setAllTestSets] = useState<TestSet[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTestSets(currentPage);
  }, [currentPage, apiKey]);

  const handleDeleteTestSet = (testSet: TestSet) => {
    TestSetService.deleteTestSet(testSet.id.toString(), null, apiKey).subscribe(
      () => {
        message.success({
          content: "Test set deleted successfully",
          style: { marginTop: "20px" },
        });
        // Refresh the current page
        fetchTestSets(currentPage);
      },
      (error: any) => {
        console.error(error);
        notification.error({
          message: "Error",
          description: `Failed to delete test set: ${error}`,
        });
      }
    );
  };

  const handleSort = (key: keyof TestSet) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Re-apply sorting and pagination when sort config, page, or data changes
  useEffect(() => {
    if (allTestSets.length > 0) {
      // Apply client-side sorting and pagination
      let sortedData = [...allTestSets];
      if (sortConfig.key) {
        sortedData.sort((a, b) => {
          const aValue = a[sortConfig.key!];
          const bValue = b[sortConfig.key!];
          
          if (aValue < bValue) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        });
      }
      
      // Apply pagination
      const offset = (currentPage - 1) * pageSize;
      const startIndex = offset;
      const endIndex = startIndex + pageSize;
      const paginatedTestSets = sortedData.slice(startIndex, endIndex);
      
      setTestSets(paginatedTestSets);
    }
  }, [allTestSets, sortConfig, currentPage, pageSize]);

  const fetchTestSets = (page: number) => {
    setIsLoading(true);
    
    // Always fetch all test sets regardless of intentGroupId context
    TestSetService.getTestSets(null, apiKey).subscribe(
      (response: any) => {
        // Handle both array response and paginated response
        const allTestSets = Array.isArray(response) ? response : (response.items || []);
        const total = Array.isArray(response) ? response.length : (response.total || allTestSets.length);
        
        setAllTestSets(allTestSets);
        setTotalCount(total);
        setIsLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsLoading(false);
      }
    );
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const newTestSetPath = intentGroupId 
    ? `/intent-group/${intentGroupId}/testsets/new`
    : "/test/test-sets/new";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                {intentGroupId && (
                  <>
                    <li>
                      <button 
                        onClick={() => navigate("/intent-groups")}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        Intent Groups
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                    <li>
                      <button 
                        onClick={() => navigate(`/intent-group/${intentGroupId}`)}
                        className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                      >
                        Intent Group
                      </button>
                    </li>
                    <li className="text-muted-foreground/40">
                      <span>/</span>
                    </li>
                  </>
                )}
                <li>
                  <span className="text-maitai-green font-gc-med">Test Sets</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Test Sets
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                {intentGroupId 
                  ? "Manage test sets for this intent group"
                  : "Manage test sets for evaluating model performance across all intent groups"
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test Sets Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Table Header with Button */}
            <div className="flex items-center justify-between p-8 border-b border-border">
              <div>
                <h3 className="text-lg font-gc-med text-foreground">Test Sets</h3>
                <p className="text-sm text-muted-foreground">Create and manage test sets for model evaluation</p>
              </div>
              <Button
                onClick={() => {
                  localStorage.removeItem(NEW_TEST_SET_STORAGE_KEY);
                  navigate(newTestSetPath);
                }}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                New Test Set
              </Button>
            </div>

            {/* Use TestSetsTable Component */}
            <div className="p-8 pt-0">
              <div className="mt-6">
                <TestSetsTable
                  testSets={testSets}
                  loading={isLoading}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onDeleteTestSet={handleDeleteTestSet}
                  intentGroupId={intentGroupId}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TestSets;
